import { Fragment, useEffect, useRef, useState } from "react";
import { C1_Dialog } from "../components/C1_Dialog";
import { itx } from "../../../src/translationItx/itx";
import type { PdfInspection, PdfPagePreview } from "@estada/plan-analyzer";
import { createPdfImportRows, updatePdfImportScale, type PdfImportRowDefault } from "./C1_PdfImportSelection";
import { tagFromName } from "../../../src/models/tagFromName";
import c1_state from "../model/C1_State";
import { C1_EventKey } from "../C1_EventKey";
import "./C1_Dialog_PdfImport.scss";

export type PdfImportPageSelection = {
  page: number;
  scale: number;
  floorTag: string;
  floorName: string;
  analysisWarning?: string;
};
type Props = {
  fileName: string;
  projectName?: string;
  singleFloor?: { name: string; tag: string };
  portal?: boolean;
  pageDefaults?: readonly PdfImportRowDefault[];
  inspection: PdfInspection;
  loadPreview: (page: number, signal: AbortSignal) => Promise<PdfPagePreview>;
  checkPage: (page: number, scale: number, signal: AbortSignal) => Promise<void>;
  onConfirm: (pages: PdfImportPageSelection[]) => void;
  onCancel: () => void;
};
// Satzweise umbrechen, ohne Punkte in Dezimalzahlen oder Dateinamen aufzutrennen.
const formatImportNotice = (text: string) => text.replace(/\.\s+/g, ".\n");
export function C1_Dialog_PdfImport(props: Props) {
  const [rows, setRows] = useState(() => {
    const rows = createPdfImportRows(props.inspection, [], "", state.maxCharLimit.tag, props.pageDefaults);
    if (!props.singleFloor) return rows;
    const selectedIndex = Math.max(0, rows.findIndex(row => row.selected));
    return rows.map((row, index) => ({ ...row, selected: index === selectedIndex,
      floorName: props.singleFloor!.name, floorTag: props.singleFloor!.tag }));
  });
  const [error, setError] = useState(""),
    [viewPage, setViewPage] = useState<number>(),
    [preview, setPreview] = useState<PdfPagePreview>(),
    [previewBusy, setPreviewBusy] = useState(false),
    [previewError, setPreviewError] = useState(""),
    [zoom, setZoom] = useState(1);
  const previewAbort = useRef<AbortController>();
  const cache = useRef(new Map<number, PdfPagePreview>());
  // Counts "Ansehen" clicks per page for the import behaviour events.
  const previewClicks = useRef<Record<number, number>>({});
  const [checks, setChecks] = useState<Record<string, { warning?: string }>>({});
  const checkKey = (row: (typeof rows)[number]) => row.page + ":" + row.scale;
  const scaleValues = rows.map((row) => row.scale).join(",");
  useEffect(() => {
    const abort = new AbortController();
    // Check every page, including pages excluded by the name preselection.
    void (async () => {
      for (const row of rows) {
        const key = checkKey(row);
        const scale = Number(row.scale);
        let warning: string | undefined;
        try {
          await props.checkPage(row.page, Number.isFinite(scale) && scale >= 1 && scale <= 10000 ? scale : 100, abort.signal);
        } catch (cause) {
          if (abort.signal.aborted) return;
          warning = cause instanceof Error ? cause.message : String(cause);
        }
        if (abort.signal.aborted) return;
        setChecks((current) => ({ ...current, [key]: { warning } }));
      }
    })();
    return () => abort.abort();
  }, [props.checkPage, scaleValues]);
  const checkingSelected = rows.some((row) => row.selected && !checks[checkKey(row)]);
  useEffect(() => () => previewAbort.current?.abort(), []);
  const update = (index: number, changes: Partial<(typeof rows)[number]>) => {
    setRows((current) => current.map((row, i) => i === index ? { ...row, ...changes }
      : props.singleFloor && changes.selected ? { ...row, selected: false } : row));
    setError("");
  };
  async function view(page: number) {
    previewClicks.current[page] = (previewClicks.current[page] ?? 0) + 1;
    c1_state.eventLogger.addEvent(C1_EventKey.PdfImportPagePreviewed, {
      fileName: props.fileName,
      page,
      clickCount: previewClicks.current[page],
    });
    previewAbort.current?.abort();
    const abort = new AbortController();
    previewAbort.current = abort;
    setViewPage(page);
    setZoom(1);
    setPreview(undefined);
    setPreviewError("");
    setPreviewBusy(true);
    try {
      const result = cache.current.get(page) ?? (await props.loadPreview(page, abort.signal));
      if (abort.signal.aborted) return;
      cache.current.set(page, result);
      setPreview(result);
    } catch (cause) {
      if (!abort.signal.aborted) setPreviewError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      if (!abort.signal.aborted) setPreviewBusy(false);
    }
  }
  function confirm() {
    const selected = rows.filter((row) => row.selected);
    if (props.singleFloor && selected.length !== 1) {
      setError(itx("c1.pdfImport.selectOnePage", "Bitte genau eine Seite für die neue Etage auswählen."));
      return;
    }
    if (!selected.length) {
      setError(itx("c1.pdfImport.selectPages", "Bitte mindestens eine Seite auswählen."));
      return;
    }
    if (checkingSelected) {
      setError(itx("c1.pdfImport.waitForCheck", "Bitte die Seitenprüfung abwarten."));
      return;
    }
    if (
      selected.some((row) => !Number.isFinite(Number(row.scale)) || Number(row.scale) < 1 || Number(row.scale) > 10000)
    ) {
      setError(
        itx(
          "c1.pdfImport.invalidScale",
          "Bitte für jede ausgewählte Seite einen Maßstab zwischen 1 und 10000 angeben.",
        ),
      );
      return;
    }
    if (selected.some((row) => !row.floorName.trim() || !row.floorTag.trim())) {
      setError(itx("c1.pdfImport.missingFloorName", "Bitte den Etagennamen für jede ausgewählte Seite ausfüllen."));
      return;
    }
    const tags = new Set<string>();
    for (const row of selected) {
      const tag = row.floorTag.trim().toLocaleLowerCase();
      if (tags.has(tag)) {
        setError(itx("c1.pdfImport.duplicateFloorTag", "Etagen-Tags müssen eindeutig sein."));
        return;
      }
      tags.add(tag);
    }
    previewAbort.current?.abort();
    c1_state.eventLogger.addEvent(C1_EventKey.PdfImportConfirmed, {
      fileName: props.fileName,
      documentPageCount: props.inspection.pageCount,
      selectedPageCount: selected.length,
      pages: selected.map((row: (typeof rows)[number]) => ({ page: row.page, scale: Number(row.scale), floorName: row.floorName.trim() })),
      previewClicks: Object.values(previewClicks.current).reduce((sum, count) => sum + count, 0),
      singleFloor: !!props.singleFloor,
    });
    props.onConfirm(
      selected.map((row) => ({
        page: row.page,
        scale: Number(row.scale),
        floorTag: row.floorTag.trim(),
        floorName: row.floorName.trim(),
        analysisWarning: checks[checkKey(row)]?.warning,
      })),
    );
  }
  const renderPages = (selected: boolean) => (
    <div className="C1_pdfImportTableWrap">
      <table data-testid={selected ? "c1-pdf-import-pages" : "c1-pdf-import-excluded-pages"}>
        <thead>
          <tr>
            <th>{itx("c1.pdfImport.selection", "Import")}</th>
            <th>{itx("c1.pdfImport.pageColumn", "Seite")}</th>
            <th>{itx("c1.pdfImport.floorName", "Etagenname")}</th>
            <th>{itx("c1.pdfImport.scaleHeading", "Maßstab")}</th>
            <th>{itx("c1.pdfImport.preview", "Vorschau")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            if (row.selected !== selected) return null;
            const info = props.inspection.pages[index];
            const hasScaleInfo = !info.printedScale && (info.paperAssumption || !info.suggestedScale);
            const check = checks[checkKey(row)];
            const hasPageInfo = hasScaleInfo || !check || !!check.warning;
            return (
              <Fragment key={row.page}>
                <tr className={hasPageInfo ? "C1_pdfImportPageWithInfo" : undefined} data-testid={"c1-pdf-import-page-" + row.page}>
                  <td>
                    <input
                      type={props.singleFloor ? "radio" : "checkbox"}
                      name={props.singleFloor ? "c1-pdf-import-floor-page" : undefined}
                      aria-label={"Seite " + row.page + " importieren"}
                      checked={row.selected}
                      onChange={(e) => update(index, { selected: e.target.checked })}
                    />
                  </td>
                  <td>
                    {row.page}
                    {info.pageKind !== "floor-plan" && (
                      <div className="C1_pdfImportScaleInfo">
                        {info.pageKind === "elevation"
                          ? itx("c1.pdfImport.kindElevation", "Ansicht")
                          : info.pageKind === "section"
                            ? itx("c1.pdfImport.kindSection", "Schnitt")
                            : info.pageKind === "other"
                              ? itx("c1.pdfImport.kindOther", "Andere Zeichnung")
                              : itx("c1.pdfImport.kindUnknown", "Seitentyp nicht erkannt")}
                      </div>
                    )}
                  </td>
                  <td>
                    <input
                      aria-label={"Etagenname Seite " + row.page}
                      value={row.floorName}
                      onChange={(e) => {
                        const floorName = e.target.value;
                        const tags = [
                          ...rows
                            .filter((other) => other.page !== row.page)
                            .map((other) => other.floorTag.toLocaleUpperCase()),
                        ];
                        update(index, {
                          floorName,
                          floorTag: tagFromName(floorName, state.maxCharLimit.tag, tags),
                        });
                      }}
                    />
                  </td>
                  <td>
                    {info.printedScale ? (
                      <span className="C1_pdfImportFixedScale">1:{info.printedScale}</span>
                    ) : (
                      <>
                        <label className="C1_pdfImportScaleInput">
                          <span>1: </span>
                          <input
                            aria-label={"Maßstab Seite " + row.page}
                            type="number"
                            min={0}
                            max={10000}
                            step={50}
                            value={row.scale}
                            onChange={(e) => {
                              const scale = e.target.value;
                              setRows((current) => updatePdfImportScale(current, index, scale));
                              setError("");
                            }}
                            onBlur={(e) => {
                              // Log the committed scale once the field loses focus, not per keystroke.
                              const scale = Number(e.target.value);
                              if (!Number.isFinite(scale) || scale <= 0) return;
                              c1_state.eventLogger.addEvent(C1_EventKey.PdfImportScaleChanged, {
                                fileName: props.fileName,
                                page: row.page,
                                scale,
                                suggestedScale: info.suggestedScale,
                              });
                            }}
                          />
                        </label>
                      </>
                    )}
                  </td>
                  <td>
                    <button type="button" onClick={() => void view(row.page)}>
                      {itx("c1.pdfImport.view", "Ansehen")}
                    </button>
                  </td>
                </tr>
                {hasPageInfo && (
                  <tr className="C1_pdfImportPageInfo">
                    <td colSpan={5}>
                      <div className="C1_pdfImportScaleInfo C1_pdfImportPageNote">
                        {hasScaleInfo && info.paperAssumption && (
                          <div>
                            {itx("c1.pdfImport.paperScale", "Annahme aus Papierformat:")} {info.paperAssumption.format}{" "}
                            → 1 : {info.paperAssumption.scale}.{" "}
                            {itx("c1.pdfImport.paperScaleWarning", "Nur ein Vorschlag; bitte am Plan prüfen.")}
                          </div>
                        )}
                        {hasScaleInfo && !info.suggestedScale && (
                          <div>{itx("c1.pdfImport.enterScale", "Maßstab bitte selbst angeben.")}</div>
                        )}
                        {!check && <div role="status">{itx("c1.pdfImport.checkingPage", "Vektorgrafiken und Importierbarkeit werden geprüft …")}</div>}
                        {check?.warning && (
                          <div className="C1_pdfImportPageWarning" role="alert" data-testid={"c1-pdf-import-warning-" + row.page}>
                            <svg className="C1_pdfImportWarningIcon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                              <path d="M12 3 2 21h20L12 3Z" />
                              <path d="M12 9v5" />
                              <circle cx="12" cy="17" r="0.8" fill="currentColor" stroke="none" />
                            </svg>
                            <div className="C1_pdfImportWarningText">
                              <strong className="C1_pdfImportWarningTitle">
                                {itx("c1.pdfImport.noAutomaticRooms", "Keine automatische Raumerkennung")}
                              </strong>
                              <div className="C1_pdfImportWarningConsequence">
                                {formatImportNotice(itx("c1.pdfImport.backgroundOnly", "Die PDF-Seite wird trotzdem importiert und kann als Hintergrund hinterlegt werden. Räume werden nicht automatisch erstellt."))}
                              </div>
                              <div className="C1_pdfImportWarningDetails">
                                {itx("c1.pdfImport.pageRejected", "Seite vom Analyser abgelehnt:")} {formatImportNotice(check.warning)}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
  const excludedCount = rows.filter((row) => !row.selected).length;
  return (
    <C1_Dialog
      open
      draggable={false}
      portal={props.portal ?? true}
      noValidate
      title={props.singleFloor ? itx("c1.pdfImport.createFloorTitle", "Etage aus PDF erstellen") : itx("c1.pdfImport.createProjectTitle", "Projekt aus PDF-File erstellen: {{name}}", {
        "{{name}}": props.projectName ?? props.fileName.replace(/\.pdf$/i, ""),
      })}
      className={`C1_pdfImportDialog${viewPage !== undefined ? " C1_pdfImportDialog--preview" : ""}`}
      showActions={viewPage === undefined}
      onCancel={() => {
        c1_state.eventLogger.addEvent(C1_EventKey.PdfImportCancelled, {
          fileName: props.fileName,
          stage: "dialog",
          previewClicks: Object.values(previewClicks.current).reduce((sum, count) => sum + count, 0),
          singleFloor: !!props.singleFloor,
        });
        props.onCancel();
      }}
      onConfirm={confirm}
      confirmDisabled={checkingSelected}
      confirmLabel={props.singleFloor ? itx("c1.pdfImport.createFloor", "Etage erstellen") : itx("c1.pdfImport.createProject", "Projekt erstellen")}
    >
      {viewPage === undefined && (
        <>
          {renderPages(true)}
          {excludedCount > 0 && (
            <details className="C1_pdfImportExcluded" data-testid="c1-pdf-import-excluded">
              <summary>
                {itx("c1.pdfImport.excludedPages", "Folgende Seiten des Dokuments werden nicht importiert …")} (
                {excludedCount})
              </summary>
              {renderPages(false)}
            </details>
          )}
          <p>
            {itx("c1.pdfImport.documentPageCount", "Das PDF Dokument hat insgesamt {{count}} Seiten", {
              "{{count}}": String(props.inspection.pageCount),
            })}
          </p>
          {error && <p role="alert">{error}</p>}
        </>
      )}
      {viewPage !== undefined && (
        <section className="C1_pdfImportPreview" data-testid="c1-pdf-import-preview">
          <div className="C1_pdfImportPreviewToolbar">
            <button
              type="button"
              data-testid="c1-pdf-import-back"
              onClick={() => {
                previewAbort.current?.abort();
                setPreviewBusy(false);
                setViewPage(undefined);
              }}
            >
              {itx("c1.pdfImport.back", "Zurück")}
            </button>
            <strong>
              {itx("c1.pdfImport.previewPage", "Seite")} * {viewPage}
            </strong>
            <div className="C1_pdfImportPreviewZoom">
              <button type="button" disabled={!preview} onClick={() => setZoom((value) => Math.max(1, value / 1.25))}>
                −
              </button>
              <button type="button" disabled={!preview} onClick={() => setZoom((value) => Math.min(8, value * 1.25))}>
                +
              </button>
              <button type="button" onClick={() => setZoom(1)}>
                {itx("c1.pdfImport.fit", "Einpassen")}
              </button>
            </div>
          </div>
          {previewBusy && <p role="status">{itx("c1.pdfImport.loadingPreview", "Originalgrafik wird geladen …")}</p>}
          {previewError && <p role="alert">{previewError}</p>}
          {preview && (
            <>
              <div className="C1_pdfImportPreviewViewport">
                <div style={{ width: zoom * 100 + "%" }} dangerouslySetInnerHTML={{ __html: preview.svg }} />
              </div>
            </>
          )}
        </section>
      )}
    </C1_Dialog>
  );
}
